import { FilePenLine, MoreVertical, Trash2, RefreshCw } from '@/components/icons';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { useCrewDelete, useCrewGetById, useCrewRestore } from '../lib/hooks/crew';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { CrewDrawer } from './CrewDrawer';

interface CrewActionsSelectProps {
  crewId: string;
  onCrewDeleted: VoidFunction;
}

export function CrewActionsSelect({
  crewId,
  onCrewDeleted,
}: CrewActionsSelectProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  const { mutate: deleteCrew } = useCrewDelete(crewId);
  const { mutate: restoreCrew } = useCrewRestore();
  const { data: crew } = useCrewGetById(crewId);

  const isDeleted = crew?.isDeleted === true;

  const handleEdit = () => {
    setIsEditDrawerOpen(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleRestore = () => {
    restoreCrew(
      { id: crewId },
      {
        onSuccess: () => {
          toast.success('Crew member activated successfully');
        },
        onError: (error) => {
          toast.error(error instanceof Error && error.message ? error.message : 'Failed to activate crew member');
        },
      }
    );
  };

  const handleDeleteCrew = () => {
    deleteCrew(
      { id: crewId },
      {
        onSuccess: () => {
          toast.success('Crew member deleted successfully');
          setShowDeleteDialog(false);
          onCrewDeleted();
        },
        onError: (error) => {
          console.error('Error deleting crew member:', error);
          toast.error(error instanceof Error && error.message ? error.message : 'Failed to delete crew member');
          setShowDeleteDialog(false);
        },
      }
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="size-8 p-0 bg-content-primary-inverse hover:bg-content-primary-inverse/30 border-0 rounded-full flex items-center justify-center outline-none">
          <MoreVertical className="size-4 text-content-primary-bold" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {isDeleted ? (
            <DropdownMenuItem onClick={handleRestore}>
              <RefreshCw className="size-4" />
              Activate Crew Member
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onClick={handleEdit}>
                <FilePenLine className="size-4" />
                Edit Crew Member
              </DropdownMenuItem>
              {!crew?.isOwner && (
                <DropdownMenuItem variant="destructive" onClick={handleDeleteClick}>
                  <Trash2 className="size-4" />
                  Delete Crew Member
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Crew Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{crew?.name}"? You can activate the crew back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCrew}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Crew Drawer */}
      <CrewDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        crewId={crewId}
        onSuccess={() => setIsEditDrawerOpen(false)}
      />
    </>
  );
}
